import { useReducer, useEffect } from 'react';
import { useNavigation, useRoute } from '@react-navigation/native';
import { useSelector } from 'react-redux';
import { selectExpenseDistanceRates } from '../store/selectors/expense.selector';
import { Modals } from '../constants/ScreenNames';
import moment from 'moment';
import { DEFAULT_BILLABLE_OPTION } from '../constants/Enums';

const TEXT_INPUTS_KEYS = {
  CLIENT: {
    key: 'CLIENT',
    placeholder: 'Client',
  },
  PARTICIPANTS: {
    key: 'PARTICIPANTS',
    placeholder: 'Participants',
  },
  FROM: {
    key: 'FROM',
    placeholder: 'From',
  },
  TO: {
    key: 'TO',
    placeholder: 'To',
  },
  LOCATION: {
    key: 'LOCATION',
    placeholder: 'Location',
  },
  PICKUP_ADDRESS: {
    key: 'PICKUP_ADDRESS',
    placeholder: 'Pickup Address',
  },
  DROPOFF_ADDRESS: {
    key: 'DROPOFF_ADDRESS',
    placeholder: 'Dropoff Address',
  },
};

const {
  CLIENT,
  PARTICIPANTS,
  FROM,
  TO,
  LOCATION,
  PICKUP_ADDRESS,
  DROPOFF_ADDRESS,
} = TEXT_INPUTS_KEYS;

export const expenseActions = {
  MERCHANT: 'merchant',
  [PARTICIPANTS.key]: PARTICIPANTS.placeholder,
  [CLIENT.key]: CLIENT.placeholder,
  DATE: 'date',
  COST: 'cost',
  CURRENCY: 'Currency',
  CATEGORY: 'category',
  ATTENDEES: 'attendees',
  REIMBURSABLE: 'reimbursable',
  BILLABLE_TYPE: 'billableType',
  STARTDISTANCE: 'startDistance',
  STARTDISTANCEIMAGE: 'startDistanceImage',
  ENDDISTANCE: 'endDistance',
  ENDDISTANCEIMAGE: 'endDistanceImage',
  TOTALDISTANCE: 'totalDistance',
  DISTANCERATE: 'distanceRate',
  DESC: 'desc',
  INPUT_TYPE: 'inputType',
  CATEGORY_AIRFARE: 'Airfare',
  CATEGORY_TRAIN: 'Train',
  CATEGORY_CAR_RENTAL: 'Car Rental',
  CATEGORY_HOTEL_STAY: 'Hotel Stay',
  CATEGORY_RIDE_SHARE: 'Ride Share',
  CATEGORY_TAXI: 'Taxi',
  CATEGORY_SERVICE_FEES: 'Service Fees',
  CATEGORY_AIRPORT_PARKING: 'Airport Parking',
  CATEGORY_OFFICE_SNACKS: 'Office Snacks',
  CATEGORY_CLIENT_MEAL: 'Client Meal',
  CATEGORY_EMPLOYEE_MEAL: 'Employee Meal',
  CATEGORY_FUEL_GAS: 'Fuel/Gas',
  CATEGORY_INTERNET_ONLINE_FEES: 'Internet/Online Fees',
  CATEGORY_MOBILE_PHONES: 'Mobile Phones',
  CATEGORY_OFFICE_SUPPLIES: 'Office Supplies',
  CATEGORY_OTHER: 'Other',
  CATEGORY_SOFTWARE: 'Software',
  CATEGORY_FOREIGN_EXCHANGE: 'Foreign Exchange',
  CATEGORY_UTILITIES: 'Utilities',
  CATEGORY_TIPS: 'Tips',
  CATEGORY_GIFTS: 'Gifts',
  CATEGORY_CLIENT_ENTERTAINMENT: 'Client Entertainment',
  CATEGORY_EMPLOYEE_ENTERTAINMENT: 'Employee Entertainment',
  CATEGORY_MARKETING: 'Marketing',
  CATEGORY_ADVERTISING: 'Advertising',
  CATEGORY_CONFERENCE: 'Conference',
  CATEGORY_CLIENT_EVENT: 'Client Event',
  CATEGORY_HARDWARE_HOSTING: 'Hardware & Hosting',
  MILEAGE_LOCATION_FROM: 'milageLocationFrom',
  CALCULATION: 'calculation',
  MILEAGE_LOCATION_TO: 'milageLocationTo',
  DISTANCE: 'distance',
  RATE: 'rate',
  SUB_CATEGORY: 'subCategory',
  ORIGIN_CITY_NAME: 'originCityName',
  DESTINATION_CITY_NAME: 'destinationCityName',
  [FROM.key]: FROM,
  [TO.key]: TO,
  [LOCATION.key]: LOCATION.placeholder,
  [PICKUP_ADDRESS.key]: PICKUP_ADDRESS.placeholder,
  [DROPOFF_ADDRESS.key]: DROPOFF_ADDRESS.placeholder,
  PURPOSE: 'purpose',
  SHOW_COST_CENTER: 'showCostCenter',
  SHOW_BILLABLE: 'showBillable',
  COST_CENTER: 'costCenter',
  LOCATION: 'Location',
  AIRFARE_CATEGORY: {
    inputType: ['text', 'text', 'text'],
    labels: ['', '', ''],
    icons: ['building', 'mark', 'mark'],
    placeholders: ['Carrier', FROM.placeholder, TO.placeholder],
  },

  TRAIN_CATEGORY: {
    inputType: ['text', 'dropdown', 'dropdown'],
    labels: ['', 'Origin', 'Destination'],
    icons: ['building', '', ''],
    placeholders: ['Rail Service', '', ''],
  },
  CAR_RENTAL_CATEGORY: {
    inputType: ['text', 'text'],
    labels: ['', '', ''],
    icons: ['building', ''],
    placeholders: ['Merchant'],
  },

  HOTEL_STAY_CATEGORY: {
    inputType: ['text', 'text'],
    labels: ['', '', ''],
    icons: ['building', 'mark'],
    placeholders: ['Merchant', LOCATION.placeholder],
  },

  RIDE_SHARE_CATEGORY: {
    inputType: ['text', 'text', 'text'],
    labels: ['', '', ''],
    icons: ['', 'mark', 'mark'],
    placeholders: ['Taxi Name', PICKUP_ADDRESS.placeholder, DROPOFF_ADDRESS.placeholder],
  },

  SERVICE_FEES_CATEGORY: {
    inputType: ['text'],
    labels: [''],
    icons: ['building'],
    placeholders: ['Name of Provider'],
  },

  AIRPORT_PARKING_CATEGORY: {
    inputType: ['text'],
    labels: [''],
    icons: ['building'],
    placeholders: ['Airport Name'],
  },

  OFFICE_SNACKS_CATEGORY: {
    inputType: ['text'],
    labels: [''],
    placeholders: ['Merchant'],
    icons: ['building'],
  },

  CLIENT_MEAL_CATEGORY: {
    inputType: ['text', 'text', 'text'],
    labels: ['', '', ''],
    placeholders: ['Merchant', PARTICIPANTS.placeholder, CLIENT.placeholder],
    icons: ['building', 'person', 'person'],
  },

  EMPLOYEE_MEAL_CATEGORY: {
    inputType: ['text', 'text'],
    labels: ['', ''],
    placeholders: ['Merchant', PARTICIPANTS.placeholder],
    icons: ['building', 'person'],
  },

  INTERNET_ONLINE_FEES_CATEGORY: {
    inputType: ['text'],
    labels: [''],
    icons: ['building'],
    placeholders: ['Provider'],
  },

  MOBILE_PHONES_CATEGORY: {
    inputType: ['text'],
    labels: [''],
    icons: ['building'],
    placeholders: ['Merchant'],
  },

  OFFICE_SUPPLIES_CATEGORY: {
    inputType: ['text'],
    labels: [''],
    icons: ['building'],
    placeholders: ['Supplies Name'],
  },

  OTHER_CATEGORY: {
    inputType: ['text'],
    labels: [''],
    icons: ['building'],
    placeholders: ['Merchant'],
  },

  SOFTWARE_CATEGORY: {
    inputType: ['text'],
    labels: [''],
    icons: ['building'],
    placeholders: ['Software Name'],
  },

  UTILITIES_CATEGORY: {
    inputType: ['text'],
    labels: [''],
    icons: ['building'],
    placeholders: ['Utility Name'],
  },
  TIPS_CATEGORY: {
    inputType: ['text'],
    labels: [''],
    icons: ['building'],
    placeholders: ['Name of Service'],
  },

  GIFTS_CATEGORY: {
    inputType: ['text'],
    labels: [''],
    icons: ['building'],
    placeholders: ['Merchant'],
  },

  CLIENT_ENTERTAINMENT_CATEGORY: {
    inputType: ['text'],
    labels: [''],
    icons: ['building'],
    placeholders: ['Merchant'],
  },

  EMPLOYEE_ENTERTAINMENT_CATEGORY: {
    inputType: ['text'],
    labels: [''],
    icons: ['building'],
    placeholders: ['Merchant'],
  },

  MARKETING_CATEGORY: {
    inputType: ['text'],
    labels: [''],
    icons: ['building'],
    placeholders: ['Merchant'],
  },

  ADVERTISING_CATEGORY: {
    inputType: ['text'],
    labels: [''],
    icons: ['building'],
    placeholders: ['Merchant'],
  },

  CONFERENCE_CATEGORY: {
    inputType: ['text'],
    labels: [''],
    icons: ['building'],
    placeholders: ['Merchant'],
  },

  CLIENT_EVENT_CATEGORY: {
    inputType: ['text'],
    labels: [''],
    icons: ['building'],
    placeholders: ['Merchant'],
  },

  HARDWARE_HOSTING_CATEGORY: {
    inputType: ['text'],
    labels: [''],
    icons: ['building'],
    placeholders: ['Merchant'],
  },
};

const initialState = {
  image: null,
  [expenseActions.MERCHANT]: '',
  [expenseActions.PARTICIPANTS]: '',
  [expenseActions.CLIENT]: '',
  [expenseActions.DATE]: '',
  currency: 'USD',
  [expenseActions.COST]: '',
  [expenseActions.CATEGORY]: '',
  [expenseActions.ATTENDEES]: [],
  userBelongToExpense: true,
  [expenseActions.REIMBURSABLE]: false,
  [expenseActions.BILLABLE_TYPE]: DEFAULT_BILLABLE_OPTION.label,
  [expenseActions.SHOW_COST_CENTER]: false,
  [expenseActions.SHOW_BILLABLE]: false,
  [expenseActions.PURPOSE]: '',
  [expenseActions.COST_CENTER]: '',
  [expenseActions.STARTDISTANCE]: '',
  [expenseActions.STARTDISTANCEIMAGE]: null,
  [expenseActions.ENDDISTANCE]: '',
  [expenseActions.ENDDISTANCEIMAGE]: null,
  [expenseActions.TOTALDISTANCE]: 0,
  unit: 'miles',
  [expenseActions.DISTANCERATE]: 'MI',
  desc: '',
  errors: {},
  [expenseActions.INPUT_TYPE]: null,
  [expenseActions.MILEAGE_LOCATION_FROM]: '',
  [expenseActions.CALCULATION]: 'Address',
  [expenseActions.MILEAGE_LOCATION_TO]: '',
  [expenseActions.DISTANCE]: 0,
  [expenseActions.RATE]: '',
  [expenseActions.SUB_CATEGORY]: 0,
  [expenseActions.ORIGIN_CITY_NAME]: '',
  [expenseActions.DESTINATION_CITY_NAME]: '',
};

export function ManualCategoryField(selectedSubCategory) {
  let obj = {};
  switch (selectedSubCategory.Name) {
    case expenseActions.CATEGORY_AIRFARE:
      obj = { ...expenseActions.AIRFARE_CATEGORY };
      break;
    case expenseActions.CATEGORY_TRAIN:
      obj = { ...expenseActions.TRAIN_CATEGORY };
      break;
    case expenseActions.CATEGORY_CAR_RENTAL:
      obj = { ...expenseActions.CAR_RENTAL_CATEGORY };
      break;
    case expenseActions.CATEGORY_HOTEL_STAY:
      obj = { ...expenseActions.HOTEL_STAY_CATEGORY };
      break;
    case expenseActions.CATEGORY_RIDE_SHARE:
    case expenseActions.CATEGORY_TAXI:
      obj = { ...expenseActions.RIDE_SHARE_CATEGORY };
      break;
    case expenseActions.CATEGORY_SERVICE_FEES:
      obj = { ...expenseActions.SERVICE_FEES_CATEGORY };
      break;
    case expenseActions.CATEGORY_AIRPORT_PARKING:
      obj = { ...expenseActions.AIRPORT_PARKING_CATEGORY };
      break;
    case expenseActions.CATEGORY_OFFICE_SNACKS:
      obj = { ...expenseActions.OFFICE_SNACKS_CATEGORY };
      break;
    case expenseActions.CATEGORY_CLIENT_MEAL:
      obj = { ...expenseActions.CLIENT_MEAL_CATEGORY };
      break;
    case expenseActions.CATEGORY_EMPLOYEE_MEAL:
      obj = { ...expenseActions.EMPLOYEE_MEAL_CATEGORY };
      break;
    case expenseActions.CATEGORY_FUEL_GAS:
    case expenseActions.CATEGORY_OTHER:
    case expenseActions.CATEGORY_FOREIGN_EXCHANGE:
      obj = { ...expenseActions.OTHER_CATEGORY };
      break;
    case expenseActions.CATEGORY_INTERNET_ONLINE_FEES:
      obj = { ...expenseActions.INTERNET_ONLINE_FEES_CATEGORY };
      break;
    case expenseActions.CATEGORY_MOBILE_PHONES:
      obj = { ...expenseActions.MOBILE_PHONES_CATEGORY };
      break;
    case expenseActions.CATEGORY_OFFICE_SUPPLIES:
      obj = { ...expenseActions.OFFICE_SUPPLIES_CATEGORY };
      break;
    case expenseActions.CATEGORY_SOFTWARE:
      obj = { ...expenseActions.SOFTWARE_CATEGORY };
      break;
    case expenseActions.CATEGORY_UTILITIES:
      obj = { ...expenseActions.UTILITIES_CATEGORY };
      break;
    case expenseActions.CATEGORY_TIPS:
      obj = { ...expenseActions.TIPS_CATEGORY };
      break;

    case expenseActions.CATEGORY_GIFTS:
      obj = { ...expenseActions.GIFTS_CATEGORY };
      break;

    case expenseActions.CATEGORY_CLIENT_ENTERTAINMENT:
      obj = { ...expenseActions.CLIENT_ENTERTAINMENT_CATEGORY };
      break;

    case expenseActions.CATEGORY_EMPLOYEE_ENTERTAINMENT:
      obj = { ...expenseActions.EMPLOYEE_ENTERTAINMENT_CATEGORY };
      break;

    case expenseActions.CATEGORY_MARKETING:
      obj = { ...expenseActions.MARKETING_CATEGORY };
      break;

    case expenseActions.CATEGORY_ADVERTISING:
      obj = { ...expenseActions.ADVERTISING_CATEGORY };
      break;

    case expenseActions.CATEGORY_CONFERENCE:
      obj = { ...expenseActions.CONFERENCE_CATEGORY };
      break;

    case expenseActions.CATEGORY_CLIENT_EVENT:
      obj = { ...expenseActions.CLIENT_EVENT_CATEGORY };
      break;

    case expenseActions.CATEGORY_HARDWARE_HOSTING:
      obj = { ...expenseActions.HARDWARE_HOSTING_CATEGORY };
      break;
  }
  return obj;
}

const reducer = (state, action) => {
  switch (action.type) {
    case expenseActions.STARTDISTANCE: {
      let totalDistance = state.totalDistance;
      totalDistance = Number(state.endDistance || 0) - Number(action.value);
      return {
        ...state,
        [action.type]: action.value,
        totalDistance: totalDistance > 0 ? totalDistance : 0,
      };
    }
    case expenseActions.ENDDISTANCE: {
      let totalDistance = state.totalDistance;
      totalDistance = Number(action.value) - Number(state.startDistance || 0);
      return {
        ...state,
        [action.type]: action.value,
        totalDistance: totalDistance > 0 ? totalDistance : 0,
      };
    }
    case expenseActions.ATTENDEES: {
      return {
        ...state,
        [action.type]: action.value.attendees,
        userBelongToExpense: action.value.userBelongToExpense,
      };
    }
    case 'errors':
      return {
        ...state,
        errors: action.value,
      };
    case expenseActions.MILEAGE_LOCATION_FROM:
      return {
        ...state,
        milageLocationFrom: action.value,
      };
    case expenseActions.MILEAGE_LOCATION_TO:
      return {
        ...state,
        milageLocationTo: action.value,
      };
    case expenseActions.DISTANCE:
      return {
        ...state,
        distance: action.value,
      };
    case expenseActions.RATE:
      return {
        ...state,
        rate: action.value,
      };
    case expenseActions.DISTANCERATE:
      return {
        ...state,
        distanceRate: action.value,
      };
    case expenseActions.COST:
      return {
        ...state,
        cost: action.value,
      };
    case expenseActions.CALCULATION:
      return {
        ...state,
        calculation: action.value,
      };
    case expenseActions.DATE:
      return {
        ...state,
        date: action.value,
      };
    case expenseActions.CATEGORY:
      return {
        ...state,
        category: action.value,
      };
    case expenseActions.SUB_CATEGORY:
      return {
        ...state,
        subCategory: action.value,
      };
    case expenseActions.REIMBURSABLE:
      return {
        ...state,
        reimbursable: action.value,
      };
    case expenseActions.BILLABLE_TYPE:
      return {
        ...state,
        billableType: action.value,
      };
    case expenseActions.PURPOSE:
      return {
        ...state,
        purpose: action.value,
      };
    case expenseActions.COST_CENTER:
      return {
        ...state,
        costCenter: action.value,
      };
    default: {
      let errors = state.errors;
      if (state.errors[action.type]) {
        errors = Object.assign(state.errors, {});
        delete errors[action.type];
      }

      return {
        ...state,
        [action.type]: action.value,
        errors,
      };
    }
  }
};

const getInitialState = (initState) => {
  return {
    ...initialState,
    ...initState,
  };
};

const useExpenseWidget = (initState = {}, options = {}) => {
  const route = useRoute();
  const navigation = useNavigation();
  const [state, dispatch] = useReducer(reducer, getInitialState(initState));
  const { selectedSubCategory, selectedAttendees, selectedDates, userBelongToExpense } =
    route?.params || {};
  const expenseDistanceRates = useSelector(selectExpenseDistanceRates);

  const openCalendar = () => {
    navigation.navigate(Modals.Calendar, {
      returnScreen: route.name,
      minDate: moment(new Date()).subtract(331, 'days').format('YYYY-MM-DD'),
      maxDate: moment(new Date()).add(331, 'days').format('YYYY-MM-DD'),
      pastScrollRange: 10,
      futureScrollRange: 10,
      fromDate: state.date,
      fromMileageTab: true,
    });
  };

  const validate = (items = {}) => {
    const errors = {};
    if (items[expenseActions.MERCHANT] && !state[expenseActions.MERCHANT]) {
      errors[expenseActions.MERCHANT] = 'Required';
    }
    if (!state[expenseActions.DATE]) {
      errors[expenseActions.DATE] = 'Required';
    }
    if (isNaN(state[expenseActions.COST]) || Number(state[expenseActions.COST]) <= 0) {
      errors[expenseActions.COST] = 'Required';
    }
    if (
      items[expenseActions.SUB_CATEGORY] &&
      isNaN(state[expenseActions.SUB_CATEGORY].SubCategoryId)
    ) {
      errors[expenseActions.SUB_CATEGORY] = 'Required';
    }
    if (
      items[expenseActions.ATTENDEES] &&
      state[expenseActions.ATTENDEES].length === 0 &&
      !state.userBelongToExpense
    ) {
      errors[expenseActions.ATTENDEES] = 'Required';
    }
    dispatch({ type: 'errors', value: errors });

    return Object.keys(errors).length === 0;
  };

  useEffect(() => {
    if (selectedDates?.startDate) {
      dispatch({ type: 'date', value: selectedDates?.startDate });
    }
  }, [selectedDates]);

  useEffect(() => {
    if (options.enableCategoryEdit && !isNaN(selectedSubCategory?.SubCategoryId)) {
      dispatch({ type: expenseActions.SUB_CATEGORY, value: selectedSubCategory });
    }
  }, [selectedSubCategory, options.enableCategoryEdit]);
  useEffect(() => {
    if (selectedSubCategory) {
      const obj = ManualCategoryField(selectedSubCategory);
      dispatch({ type: expenseActions.INPUT_TYPE, value: obj });
    }
  }, [selectedSubCategory]);

  useEffect(() => {
    if (state.Client) {
      dispatch({ type: expenseActions.CLIENT, value: state.Client });
    }
  }, [state.Client]);

  useEffect(() => {
    if (state.Participants) {
      dispatch({ type: expenseActions.PARTICIPANTS, value: state.Participants });
    }
  }, [state.Participants]);

  useEffect(() => {
    if (options.enableAttendeesEdit && selectedAttendees && userBelongToExpense !== null) {
      dispatch({
        type: expenseActions.ATTENDEES,
        value: { attendees: selectedAttendees, userBelongToExpense },
      });
    }
  }, [selectedAttendees, userBelongToExpense, options.enableAttendeesEdit]);

  useEffect(() => {
    if (state.totalDistance > 0 && expenseDistanceRates?.[state.distanceRate]) {
      const cost = expenseDistanceRates[state.distanceRate].Rate;
      dispatch({ type: expenseActions.COST, value: cost * state.totalDistance });
    }
  }, [state.totalDistance, state.distanceRate, expenseDistanceRates]);

  return [state, dispatch, openCalendar, validate];
};

export default useExpenseWidget;
